"use client";

import { format, parseISO } from "date-fns";
import { useAtom } from "jotai";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { purchaseOrdersAtom } from "~/atoms";
import AddressField, { Address } from "~/components/address/AddressField";
import Button from "~/components/buttons/Button";
import DatePickerButton from "~/components/datePicker/DatePickerButton";
import FileUploader from "~/components/FileUploader/FileUploader";
import Flex from "~/components/flex/Flex";
import TextArea from "~/components/inputs/TextArea";
import { TextInput } from "~/components/inputs/TextInput";
import AndercoreLogoLoadingGif from "~/components/loader/AndercoreLogoLoadingGif";
import Text from "~/components/texts/Text";
import { ENDPOINT } from "~/constants/endpoints";
import { QUERY_PARAM_PURCHASE_ORDER } from "~/constants/queryParams";
import { useGet, usePatch } from "~/hooks/useApi";
import { useI18n } from "~/locales/client";
import { AddressType, ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import {
  AttachmentType,
  FileRequestType,
  IncotermTypeEnum,
  PofeStatusTypeEnum,
  PofeType,
  PurchaseOrderType,
} from "~/types-and-enums/purchaseOrderTypes";
import { removeItemFromLocalStorage, updateSearchParams } from "~/utils/clientUtils";
import { formatDate } from "~/utils/dateUtils";
import { fileToBase64 } from "~/utils/generalUtils";
import { deleteFilesFromIdb, getFilesFromIdb, saveFilesToIdb } from "~/utils/indexedDbFileStore";
import POFEPProductDetails from "./POFEPProductDetails";
import UploadedFilesList from "./UploadedFilesList";
import { FILE_TYPE_FLASHLIST } from "~/constants";
import { FILE_TYPE_INVOICE } from "~/constants";

type POFEDetailsProps = {
  handleCloseModal: () => void;
  selectedPofeDetails: PofeType | undefined;
  selectedPofeNumber: string;
  selectedPONumber: string;
};

/**
 * Purchase Order Details component
 * @param purchaseOrders - The list of purchase orders
 * @returns The Purchase Order Details component
 */
const POFEDetails = ({
  handleCloseModal,
  selectedPofeDetails,
  selectedPofeNumber,
  selectedPONumber,
}: POFEDetailsProps) => {
  const searchParams = useSearchParams();
  const translate = useI18n();
  const requestedPoId = searchParams.get(QUERY_PARAM_PURCHASE_ORDER);
  const [purchaseOrdersState, setPurchaseOrdersState] = useAtom(purchaseOrdersAtom);
  const missingHandoverDate = !selectedPofeDetails?.handover_date;

  // Fetch data from API for the selected POFE
  const { data, loading } = useGet<PofeType>(ENDPOINT.purchaseOrderFulfillmentEvent.byId(selectedPofeNumber as string));
  // Get attachments from atom state
  const attachmentsFromAtomState: AttachmentType[] =
    purchaseOrdersState
      .find((order) => order.id === selectedPONumber)
      ?.po_fulfillment_events.find((pofe) => pofe.id === selectedPofeNumber)?.attachments || [];
  // Get attachments from API
  const attachmentsFromApi: AttachmentType[] = data?.attachments || [];
  // Get the final list of attachments (from atom state or API)
  const finalListOfAttachments =
    attachmentsFromAtomState.length > attachmentsFromApi.length ? attachmentsFromAtomState : attachmentsFromApi;

  const computeInitialValues = () => ({
    handoverDate: selectedPofeDetails?.handover_date
      ? parseISO(selectedPofeDetails.handover_date as string)
      : undefined,
    containerNumber: selectedPofeDetails?.container_number ?? "",
    pickUpComment: selectedPofeDetails?.pick_up_comment ?? "",
    batchNumber: selectedPofeDetails?.batch_number ?? "",
    pickupAddress: selectedPofeDetails?.pickup_address ?? null,
    deliveryAddress: selectedPofeDetails?.delivery_address ?? null,
    attachments: finalListOfAttachments,
  });

  const [initialValues, setInitialValues] = useState(computeInitialValues);
  const [handoverDate, setHandoverDate] = useState<Date | undefined>(initialValues.handoverDate);
  const [batchNumber, setBatchNumber] = useState<string>(initialValues.batchNumber);
  const [containerNumber, setContainerNumber] = useState<string>(initialValues.containerNumber);
  const [pickUpComment, setPickUpComment] = useState<string>(initialValues.pickUpComment);
  const [pickupAddress, setPickupAddress] = useState<AddressType>(initialValues.pickupAddress);
  const [deliveryAddress, setDeliveryAddress] = useState<AddressType>(initialValues.deliveryAddress);
  const [attachments, setAttachments] = useState<AttachmentType[]>(initialValues.attachments as AttachmentType[]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  // Map to track which IndexedDB ID corresponds to which file (using file key as string)
  const [fileIdMap, setFileIdMap] = useState<Map<string, string>>(new Map());
  // Map to track file type for each file (file key -> fileType)
  const [fileTypeMap, setFileTypeMap] = useState<Map<string, string>>(new Map());
  const searchParamsString = searchParams.toString();
  // Helper to create a unique key for a file
  const getFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;
  // Store file IDs in localStorage to persist across refreshes
  const STORAGE_KEY = useMemo(() => `pofe-files-${selectedPofeNumber}`, [selectedPofeNumber]);

  // Update attachments state when the final list of attachments changes
  useEffect(() => {
    if (finalListOfAttachments.length > 0) {
      setAttachments(finalListOfAttachments);
    }
  }, [finalListOfAttachments, setAttachments]);

  // Load files from IndexedDB on mount or when POFE changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!selectedPofeNumber) return; // Wait for POFE number to be available

    const loadFilesFromIdb = async () => {
      try {
        // Try to load file IDs from localStorage
        const storedIds = localStorage.getItem(STORAGE_KEY);
        if (storedIds) {
          const fileIds = JSON.parse(storedIds) as string[];
          if (fileIds.length > 0) {
            const files = await getFilesFromIdb(fileIds);

            // Create original file objects (without prefix) for display
            const originalFiles = files.map((file) => {
              // Extract type from filename prefix (e.g., "Flashlist_filename.pdf" -> "Flashlist")
              const prefixMatch = file.name.match(/^(Flashlist|Invoice)_(.+)$/);
              if (prefixMatch) {
                const [, , originalName] = prefixMatch;
                return new File([file], originalName, {
                  type: file.type,
                  lastModified: file.lastModified,
                });
              }
              return file;
            });

            setUploadedFiles(originalFiles);
            setUploadedFileIds(fileIds);

            // Rebuild file ID map and file type map
            const newIdMap = new Map<string, string>();
            const newTypeMap = new Map<string, string>();

            originalFiles.forEach((file, index) => {
              const fileKey = getFileKey(file);
              newIdMap.set(fileKey, fileIds[index]);

              // Extract file type from prefixed filename
              const prefixMatch = files[index].name.match(/^(Flashlist|Invoice)_/);
              if (prefixMatch) {
                newTypeMap.set(fileKey, prefixMatch[1]);
              }
            });

            setFileIdMap(newIdMap);
            setFileTypeMap(newTypeMap);
          }
        }
      } catch (error) {
        console.error("Failed to load files from IndexedDB:", error);
      }
    };

    loadFilesFromIdb();
  }, [selectedPofeNumber, STORAGE_KEY]);

  // Business rule helpers (after state declarations)
  const status = selectedPofeDetails?.status as PofeStatusTypeEnum | string | undefined;
  const isCancelled = status === PofeStatusTypeEnum.CANCELLED;
  const isDelivered = status === PofeStatusTypeEnum.DELIVERED;
  const incoterm = selectedPofeDetails?.incoterm?.toUpperCase() || "";
  const incotermLocation = selectedPofeDetails?.incoterm_location || "";
  const deadlineForHandover = selectedPofeDetails?.deadline_for_handover || "";
  const actualPickupDate = selectedPofeDetails?.actual_pickup_date || "";

  // Determine which address to use based on incoterm
  const isDDPOrDAPOrFOB = [
    IncotermTypeEnum.DDP,
    IncotermTypeEnum.DAP,
    IncotermTypeEnum.DDP_DAP,
    IncotermTypeEnum.FOB,
  ].includes(incoterm as unknown as IncotermTypeEnum);
  const shouldUseDeliveryAddress = isDDPOrDAPOrFOB;
  const shouldUsePickupAddress = !isDDPOrDAPOrFOB;

  // Address editing rules
  // Check if address was already saved (in initialValues), not current state
  const hasSavedAddress = shouldUseDeliveryAddress
    ? Boolean(
        initialValues.deliveryAddress?.postal_code ||
          initialValues.deliveryAddress?.city ||
          initialValues.deliveryAddress?.country
      )
    : Boolean(
        initialValues.pickupAddress?.postal_code ||
          initialValues.pickupAddress?.city ||
          initialValues.pickupAddress?.country
      );
  // Allow editing only if not DDP or DAP or status is cancelled or delivered or address has already been saved
  const canEditAddress = !isCancelled && !isDelivered && !hasSavedAddress && !isDDPOrDAPOrFOB;

  // Field disabled states
  const canEditHandoverDate = !isCancelled && !isDelivered && !selectedPofeDetails?.handover_date;
  const canEditOtherFields = !isCancelled && !isDelivered;
  const canUploadFiles = !isCancelled; // Can upload files unless cancelled

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!selectedPONumber) return;
    if (requestedPoId === selectedPONumber) return;

    const params = new URLSearchParams(searchParamsString);
    params.set(QUERY_PARAM_PURCHASE_ORDER, selectedPONumber);
    updateSearchParams(params);
  }, [requestedPoId, selectedPONumber, searchParamsString]);

  const updateEndpoint = selectedPofeDetails?.id
    ? ENDPOINT.purchaseOrderFulfillmentEvent.update(selectedPofeDetails.id)
    : null;

  const { execute: updateFulfillmentEvent, loading: isUpdating = false } = usePatch<
    PurchaseOrderType,
    {
      handover_date: string | null;
      container_number: string;
      pick_up_comment: string;
      batch_number: string;
      pickup_address: AddressType;
      delivery_address: AddressType;
      attachments: AttachmentType[];
    }
  >(updateEndpoint ?? "");

  // Update states when selected POFE ID changes (switching between POFEs)
  // Also update when data from useGet changes (after refetch) to sync form values
  useEffect(() => {
    const nextInitial = computeInitialValues();
    setInitialValues(nextInitial);
    setHandoverDate(nextInitial.handoverDate);
    setContainerNumber(nextInitial.containerNumber);
    setBatchNumber(nextInitial.batchNumber);
    setPickUpComment(nextInitial.pickUpComment);
    setPickupAddress(nextInitial.pickupAddress);
    setDeliveryAddress(nextInitial.deliveryAddress);
    setAttachments(nextInitial.attachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPofeNumber]);

  // Check if there are any changes
  const hasChanges =
    (handoverDate?.getTime() || 0) !== (initialValues.handoverDate?.getTime() || 0) ||
    containerNumber !== initialValues.containerNumber ||
    pickUpComment !== initialValues.pickUpComment ||
    batchNumber !== initialValues.batchNumber ||
    (shouldUseDeliveryAddress && JSON.stringify(deliveryAddress) !== JSON.stringify(initialValues.deliveryAddress)) ||
    (shouldUsePickupAddress && JSON.stringify(pickupAddress) !== JSON.stringify(initialValues.pickupAddress)) ||
    uploadedFiles.length > 0;

  // Handle file upload - save to IndexedDB
  const handleFileUpload = async (files: File[], fileType?: string) => {
    try {
      // Find new files that aren't already saved
      const existingFileKeys = new Set(uploadedFiles.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
      const newFiles = files.filter((f) => !existingFileKeys.has(`${f.name}-${f.size}-${f.lastModified}`));

      if (newFiles.length > 0 && fileType) {
        // Create new File objects with prefixed names for IndexedDB
        const prefixedFiles = newFiles.map((file) => {
          const prefix = `${fileType}_`;
          const prefixedName = file.name.startsWith(prefix) ? file.name : `${prefix}${file.name}`;
          return new File([file], prefixedName, {
            type: file.type,
            lastModified: file.lastModified,
          });
        });

        const fileIds = await saveFilesToIdb(prefixedFiles);
        const updatedIds = [...uploadedFileIds, ...fileIds];
        setUploadedFileIds(updatedIds);

        // Update file ID map and file type map
        setFileIdMap((prev) => {
          const newMap = new Map(prev);
          newFiles.forEach((file, index) => {
            newMap.set(getFileKey(file), fileIds[index]);
          });
          return newMap;
        });

        setFileTypeMap((prev) => {
          const newMap = new Map(prev);
          newFiles.forEach((file) => {
            newMap.set(getFileKey(file), fileType);
          });
          return newMap;
        });

        // Store in localStorage for persistence
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedIds));
        }
      }

      // Update files state (keep original files, not prefixed ones)
      setUploadedFiles(files);
    } catch (error) {
      console.error("Failed to save files to IndexedDB:", error);
      // Still update files state even if IndexedDB save fails
      setUploadedFiles(files);
    }
  };

  // Handle file removal - remove from IndexedDB
  const handleFileRemove = async (index: number) => {
    const fileToRemove = uploadedFiles[index];
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);

    // Find and remove the corresponding file ID from IndexedDB
    try {
      const fileKey = getFileKey(fileToRemove);
      const idToRemove = fileIdMap.get(fileKey);
      if (idToRemove) {
        await deleteFilesFromIdb([idToRemove]);
        const updatedIds = uploadedFileIds.filter((id) => id !== idToRemove);
        setUploadedFileIds(updatedIds);

        // Update file ID map and file type map
        setFileIdMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileKey);
          return newMap;
        });

        setFileTypeMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileKey);
          return newMap;
        });

        // Update localStorage
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedIds));
        }
      }
    } catch (error) {
      console.error("Failed to remove file from IndexedDB:", error);
    }
  };

  // Reset all states to original values
  const handleRevert = async () => {
    setHandoverDate(initialValues.handoverDate);
    setContainerNumber(initialValues.containerNumber);
    setPickUpComment(initialValues.pickUpComment);
    setPickupAddress(initialValues.pickupAddress);
    setDeliveryAddress(initialValues.deliveryAddress);
    setUploadedFiles([]);

    // Remove files from IndexedDB
    if (uploadedFileIds.length > 0) {
      try {
        await deleteFilesFromIdb(uploadedFileIds);
        setUploadedFileIds([]);
        // Clear localStorage
        if (typeof window !== "undefined") {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error("Failed to remove files from IndexedDB:", error);
      }
    }
  };

  const handleSave = async () => {
    if (!selectedPofeDetails?.id || !updateEndpoint) return;

    try {
      // Prepare attachments array with base64 encoded files
      // Format: { title, path_on_client, version_data } as per backend requirements
      const attachmentsArray: FileRequestType[] = [];

      // Convert uploaded files to base64 and format as attachments
      if (uploadedFiles.length > 0 && uploadedFileIds.length > 0) {
        try {
          // Get files from IndexedDB (these have prefixed names)
          const filesToUpload = await getFilesFromIdb(uploadedFileIds);

          // Convert each file to base64 and create attachment object
          for (let index = 0; index < filesToUpload.length; index++) {
            const file = filesToUpload[index];
            const fileId = uploadedFileIds[index];

            // Find the original file to get its type
            const originalFile = uploadedFiles.find((f) => {
              const fileKey = getFileKey(f);
              const id = fileIdMap.get(fileKey);
              return id === fileId;
            });

            if (originalFile) {
              const fileKey = getFileKey(originalFile);
              const fileType = fileTypeMap.get(fileKey) || "Document";

              // Convert file to base64
              const base64Data = await fileToBase64(file);

              // Extract original filename (remove prefix if present)
              const originalName = originalFile.name;

              // Create title based on file type: Invoice_<filename> or Flashlist_<filename>
              const title =
                fileType === FILE_TYPE_INVOICE
                  ? `${FILE_TYPE_INVOICE}_${originalName}`
                  : fileType === FILE_TYPE_FLASHLIST
                    ? `${FILE_TYPE_FLASHLIST}_${originalName}`
                    : `${fileType}_${originalName}`;

              // Create attachment object
              attachmentsArray.push({
                title,
                path_on_client: originalName,
                version_data: base64Data,
                content_size: file.size, // File size in bytes
              });
            }
          }
        } catch (error) {
          console.error("Failed to prepare files for upload:", error);
          throw error;
        }
      }

      // Prepare payload with attachments in the required format
      const payload = {
        handover_date: handoverDate ? format(handoverDate, "yyyy-MM-dd") : null,
        container_number: containerNumber,
        pick_up_comment: pickUpComment,
        batch_number: batchNumber,
        pickup_address: pickupAddress,
        delivery_address: deliveryAddress,
        attachments: attachmentsArray,
      };

      // Type assertion needed because backend expects different attachment format than TypeScript type
      await updateFulfillmentEvent(payload as unknown as Parameters<typeof updateFulfillmentEvent>[0]);

      // Clear files from IndexedDB after successful upload
      if (uploadedFileIds.length > 0) {
        await deleteFilesFromIdb(uploadedFileIds);
        removeItemFromLocalStorage(STORAGE_KEY);
        setUploadedFileIds([]);
        setUploadedFiles([]);
        setFileIdMap(new Map());
        setFileTypeMap(new Map());
      }

      // Update the atom with the saved values
      setPurchaseOrdersState((prevOrders) =>
        prevOrders.map((order) => {
          if (order.id === selectedPONumber) {
            return {
              ...order,
              po_fulfillment_events: order.po_fulfillment_events.map((pofe) => {
                if (pofe.id === selectedPofeNumber) {
                  return {
                    ...pofe,
                    handover_date: handoverDate ? format(handoverDate, "yyyy-MM-dd") : null,
                    container_number: containerNumber,
                    pick_up_comment: pickUpComment,
                    batch_number: batchNumber,
                    pickup_address: pickupAddress,
                    delivery_address: deliveryAddress,
                    attachments: [
                      ...((finalListOfAttachments as AttachmentType[]) || []),
                      ...(attachmentsArray as AttachmentType[]),
                    ],
                  };
                }
                return pofe;
              }),
            };
          }
          return order;
        })
      );

      // Update initial values with current form values
      // The refetched attachments will be updated via the useEffect that watches data?.attachments
      setInitialValues((prev) => ({
        ...prev,
        handoverDate,
        containerNumber,
        pickUpComment,
        batchNumber,
        pickupAddress,
        deliveryAddress,
        attachments: [...(finalListOfAttachments as AttachmentType[]), ...(attachmentsArray as AttachmentType[])],
      }));
      handleCloseModal();
    } catch (error) {
      console.error("Failed to update purchase order fulfillment event:", error);
    }
  };

  return (
    <Flex className="flex-col gap-4 w-full h-full">
      <Flex className="flex-col gap-6 w-full flex-1 min-h-0 overflow-y-auto hide-scrollbar pb-20">
        {/* Products */}
        <Flex className="flex-col gap-2">
          <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
            {translate("general.products")}
          </Text>
          {selectedPofeDetails && <POFEPProductDetails pofe={selectedPofeDetails} translate={translate} />}
        </Flex>
        {!isCancelled && (
          <Flex className="flex-col gap-2">
            <Flex className="flex-row gap-1 items-center">
              <Text className="font-medium text-gray-500 text-xs md:text-xs">{translate("general.incoterm")}</Text>
              <Text fontWeight="semibold" className="font-medium w-fit text-xs md:text-xs">
                : {incoterm ? incoterm : "-"} {incotermLocation ? " - " + incotermLocation : ""}
              </Text>
            </Flex>
            <Flex className="flex-row gap-1 items-center">
              <Text className="font-medium text-gray-500 text-xs md:text-xs">
                {translate("general.deadline-for-handover")}
              </Text>
              <Text fontWeight="semibold" className="font-medium w-fit text-xs md:text-xs">
                : {deadlineForHandover ? formatDate(deadlineForHandover) : "-"}
              </Text>
            </Flex>
            {!isDDPOrDAPOrFOB && (
              <Flex className="flex-row gap-1 items-center">
                <Text className="font-medium text-gray-500 text-xs md:text-xs">
                  {translate("general.actual-pickup-date")}
                </Text>
                <Text fontWeight="semibold" className="font-medium w-fit text-xs md:text-xs">
                  : {actualPickupDate ? formatDate(actualPickupDate) : "-"}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
        {/* Handover Date */}
        <Flex className="flex-col gap-[2px]">
          <Text
            color={missingHandoverDate && canEditHandoverDate ? "red" : "lightGray"}
            fontWeight="semibold"
            className="text-[8px] md:text-xs"
          >
            {translate("general.handover-date")} *
          </Text>
          <DatePickerButton
            value={handoverDate}
            classNames="w-full md:w-fit"
            isDisabled={!canEditHandoverDate || isUpdating}
            onChange={(date) => {
              setHandoverDate(date);
            }}
          />
        </Flex>
        {/* Address */}
        <Flex className="flex-col gap-[2px]">
          <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
            {translate("general.address")} *
          </Text>
          <AddressField
            value={
              (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)
                ? {
                    street: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.street || "",
                    city: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.city || "",
                    postal_code: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.postal_code || "",
                    state: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.state || null,
                    state_code: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.state_code || null,
                    country: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.country || null,
                    country_code: (shouldUseDeliveryAddress ? deliveryAddress : pickupAddress)?.country_code || null,
                  }
                : undefined
            }
            onChange={(address: Address) => {
              if (shouldUseDeliveryAddress) {
                setDeliveryAddress(address);
              } else {
                setPickupAddress(address);
              }
            }}
            isDisabled={!canEditAddress || isUpdating}
          />
        </Flex>
        {/* Pick Up reference */}
        <Flex className="flex-col gap-[2px]">
          <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
            {translate("general.pick-up-reference")}
          </Text>
          <TextArea
            value={pickUpComment}
            onChange={(value: string) => {
              setPickUpComment(value);
            }}
            placeholder={translate("general.pick-up-comment")}
            classNames="w-full"
            isDisabled={!canEditOtherFields || isUpdating}
          />
        </Flex>
        {/* Batch Number */}
        <Flex className="flex-col gap-[2px]">
          <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
            {translate("general.batch-number")}
          </Text>
          <TextInput
            value={batchNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setBatchNumber(e.target.value);
            }}
            placeholder={translate("placeholder.batch-number")}
            isDisabled={!canEditOtherFields || isUpdating}
          />
        </Flex>
        {/* Container Number */}
        <Flex className="flex-col gap-[2px]">
          <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
            {translate("general.container-number")}
          </Text>
          <TextInput
            value={containerNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setContainerNumber(e.target.value);
            }}
            placeholder={translate("placeholder.container-number")}
            classNames="w-full"
            isDisabled={!canEditOtherFields || isUpdating}
          />
        </Flex>
        {/* Warehouse Opening Hours */}
        {Boolean(Object.entries(selectedPofeDetails?.main_warehouse_opening_times || {}).length) && (
          <Flex className="flex-col gap-[2px]">
            <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
              {translate("general.warehouse-opening-hours")}
            </Text>
            <Flex className="flex-col gap-1">
              {Object.entries(selectedPofeDetails?.main_warehouse_opening_times || {}).map(([day, time]) => (
                <Flex key={day} className="gap-2">
                  <Text fontWeight="medium" className="w-24 shrink-0">
                    {translate(`general.${day.toLowerCase()}`)}:
                  </Text>
                  <Text className="text-wrap">{time as string}</Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}
        {loading ? (
          <Flex className="flex-col gap-4 w-full h-full">
            <Flex className="flex-col gap-6 w-full flex-1 min-h-0 overflow-y-auto hide-scrollbar pb-20">
              <AndercoreLogoLoadingGif className="h-24 bg-transparent" />
            </Flex>
          </Flex>
        ) : (
          <>
            {/* Display already uploaded files from backend */}
            <UploadedFilesList attachments={attachments} />
            {/* Add file uploader for flashlist files */}
            <Flex className="w-full gap-4 md:gap-8">
              <Flex className="flex-col gap-[2px] w-full">
                <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
                  {translate("general.flashlists_or_invoices")}
                </Text>
                <FileUploader
                  key={`flashlist-${selectedPofeNumber}`}
                  onChange={handleFileUpload}
                  onRemove={handleFileRemove}
                  multiple
                  fileTypeOptions={["Flashlist", "Invoice"]}
                  isDisabled={!canUploadFiles || isUpdating}
                  initialFiles={uploadedFiles}
                  getFileType={(file) => {
                    const fileKey = getFileKey(file);
                    return fileTypeMap.get(fileKey);
                  }}
                />
              </Flex>
            </Flex>
          </>
        )}
        <Flex className="gap-[2px] md:flex-row justify-between md:hidden">
          <Button
            text={translate("general.cancel")}
            onClick={handleRevert}
            buttonClassName="w-fit md:w-auto bg-white border-gray-300 md:min-w-20"
            buttonStyle={ButtonTypesEnum.default}
            disabled={!hasChanges || isUpdating}
          />
          <Button
            text={translate("general.save")}
            onClick={handleSave}
            buttonClassName="w-fit md:w-auto md:min-w-20"
            disabled={!hasChanges || isUpdating}
            isLoading={isUpdating || loading}
          />
        </Flex>
      </Flex>
      {/* Add button to submit the order, make it sticky to the bottom of the modal */}
      <Flex className="hidden md:flex gap-[2px] md:flex-row justify-between sticky bottom-0 w-[475px] lg:w-[575px] bg-white p-4 shadow-lg border-t border-gray-200 z-10 -mx-10 px-10">
        <Button
          text={translate("general.cancel")}
          onClick={handleRevert}
          buttonClassName="w-fit md:w-auto bg-white border-gray-300 md:min-w-20"
          buttonStyle={ButtonTypesEnum.default}
          disabled={!hasChanges || isUpdating}
        />
        <Button
          text={translate("general.save")}
          onClick={handleSave}
          buttonClassName="w-fit md:w-auto md:min-w-20"
          disabled={!hasChanges || isUpdating}
          isLoading={isUpdating || loading}
        />
      </Flex>
    </Flex>
  );
};

export default POFEDetails;
